/**
 * Detail-panel selection via URL search param — backs the deep-link contract
 * Slice I introduced (ADR-0089 + ADR-0090). The URL IS the source of truth
 * for which panel is open on the Firms / Users pages:
 *
 *   /firm-success/firms?firm=<id>   → Firm Detail Panel opens to <id>
 *   /firm-success/users?user=<id>   → User Detail Panel opens to <id>
 *
 * Refresh-survives, deep-linkable from the Dashboard row Identity Link, and
 * collapses to a clean URL when the panel closes.
 *
 * Mirrors `useViewerSelection`'s shape: read the param, expose a setter that
 * navigates without remounting the page. Empty / whitespace-only param is
 * treated as absent (`selectedId === null`), matching the `validateSearch`
 * collapse in router.tsx.
 *
 * Coupling: this hook is the *only* place the Firms / Users pages touch the
 * router. The pages read `{selectedId, openPanel, closePanel}` and never see
 * `useRouterState` / `useNavigate` directly — keeps the pages testable
 * without a Router provider.
 */

import { useNavigate, useRouterState } from "@tanstack/react-router";

export interface PanelSelection {
  /** The selected entity id from the URL, or null when no panel is open. */
  selectedId: string | null;
  /** Open the panel for `id` by writing the URL search param. */
  openPanel: (id: string) => void;
  /** Close the panel by clearing the URL search param. */
  closePanel: () => void;
}

/**
 * Wire URL `?<paramName>=<id>` to a panel-open state. `paramName` is the
 * search-key the route's `validateSearch` reads (`firm` for the Firms route,
 * `user` for the Users route).
 */
export function usePanelSelection(paramName: string): PanelSelection {
  const navigate = useNavigate();
  const raw = useRouterState({
    select: (s) => {
      const search = s.location.search as Record<string, unknown>;
      const v = search?.[paramName];
      return typeof v === "string" ? v.trim() : "";
    },
  });
  const selectedId = raw === "" ? null : raw;

  function openPanel(id: string) {
    const trimmed = id.trim();
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        if (trimmed === "") delete next[paramName];
        else next[paramName] = trimmed;
        return next;
      },
      replace: true,
    });
  }

  function closePanel() {
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        delete next[paramName];
        return next;
      },
      replace: true,
    });
  }

  return { selectedId, openPanel, closePanel };
}
